import { ReactNode } from 'react';
import { NeedDto, UserDto } from '@act/shared/data-transfer-objects';
import {
  getNeedStatus,
  NeedStatus,
} from '../../../../utilities/needs/getNeedStatus.util';
import { timeFormatter } from '@act/shared/utilities';
import HtmlDisplayer from '../../../../utilities/HtmlDisplayer';
import { FlexSection } from '../../../../containers/Pillbox/components';
import { NeedSummaryDateLine } from '../dateLines/NeedSummaryDateLine';
import { GoalCardActionItems } from '../../goalCardShared.types';
import { GoalCardActions } from '../actions/GoalCardActions';
import { needIsExpiringSoon } from '../../utilities/getDaysUntilNeedExpires.util';
import { Dictionary } from '@act/shared/types';

const NEED_ALERT_DURATION_IN_DAYS = 14;

interface NeedGoalSummaryProps {
  need: NeedDto;
  member: UserDto;
  actions?: GoalCardActionItems[];
  userMap?: Dictionary<UserDto>;
}

export const NeedGoalSummary = ({
  need,
  member,
  actions,
  userMap,
}: NeedGoalSummaryProps) => {
  function determineNeedSummary(): ReactNode {
    const needStatus: NeedStatus = getNeedStatus(need, member);
    const daysSinceIdentification = timeFormatter.differenceInDaysFromNow(
      need.identifiedAt
    );

    let summaryBody: ReactNode;

    switch (needStatus) {
      case NeedStatus.Fulfilled:
        summaryBody = <NeedSummaryFulfilled need={need} />;
        break;
      case NeedStatus.Identified:
        if (needIsExpiringSoon(need)) {
          summaryBody = <NeedSummaryExpiringSoon need={need} member={member} />;
        } else if (
          need.needRequiresAction &&
          Math.abs(daysSinceIdentification) >= NEED_ALERT_DURATION_IN_DAYS
        ) {
          summaryBody = <NeedSummaryNeedsAction need={need} member={member} />;
        } else {
          summaryBody = <NeedSummaryInProgress need={need} member={member} />;
        }
        break;
      case NeedStatus.StoppedPursuing:
        summaryBody = (
          <NeedSummaryStoppedPursuing need={need} userMap={userMap} />
        );
        break;
      case NeedStatus.Unresolved:
        summaryBody = <NeedSummaryUnresolved need={need} />;
        break;
      default:
        summaryBody = 'Unknown needs status.';
    }

    return (
      <FlexSection direction="column" gap={16}>
        <NeedSummaryDateLine need={need} userMap={userMap} />
        {summaryBody}
        <GoalCardActions actions={actions} />
      </FlexSection>
    );
  }

  return determineNeedSummary();
};

interface NeedSummaryContentProps {
  need?: NeedDto;
  member?: UserDto;
  userMap?: Dictionary<UserDto>;
}

const NeedSummaryExpiringSoon = ({ member }: NeedSummaryContentProps) => {
  return `Extend the target time window or work with ${member.firstName} to fulfill the need before it expires.`;
};

const NeedSummaryFulfilled = ({ need }: NeedSummaryContentProps) => {
  return <HtmlDisplayer innerHtml={need.reason} />;
};

const NeedSummaryInProgress = ({ member }: NeedSummaryContentProps) => {
  return `Continue working with ${member?.firstName} to fulfill this need.`;
};

const NeedSummaryNeedsAction = ({ member }: NeedSummaryContentProps) => {
  return `Document an action on this need to start working on it with ${member?.firstName}.`;
};

const NeedSummaryStoppedPursuing = ({
  need,
  userMap,
}: NeedSummaryContentProps) => {
  const stoppedPursuingByName = userMap?.[need.closedById]?.name || 'Unknown';

  return `${stoppedPursuingByName} stopped pursuing this need on ${timeFormatter.epochToHumanReadableDate(
    {
      date: need.abandonedAt,
      excludeRelativeDay: true,
      showFullMonthName: true,
    }
  )}.`;
};

const NeedSummaryUnresolved = ({ need }: NeedSummaryContentProps) => {
  const daysFromExpiryDate = timeFormatter.differenceInDaysFromNow(
    need.expiresAt
  );

  if (daysFromExpiryDate < 0) {
    return `This need expired on ${timeFormatter.epochToHumanReadableDate({
      date: need.expiresAt,
      excludeRelativeDay: true,
      showFullMonthName: true,
    })}.`;
  } else {
    return `This need expired because the member went out of program.`;
  }
};
